import type { PhotoModerationItemDto } from '@upme/contracts';
import { interestLabel } from '@upme/contracts';
import { Badge } from '@upme/ui/components/badge';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { useState } from 'react';
import { toast } from 'sonner';
import { formatEnergyFull } from '../../../shared/format-energy';
import { useDecidePhotoModeration } from '../mutations';

interface Props {
  items: PhotoModerationItemDto[];
}

export function PhotoModerationGrid({ items }: Props) {
  if (items.length === 0) {
    return <p className="text-muted-foreground">Нет фото, ожидающих модерации.</p>;
  }
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => (
        <PhotoModerationCard key={item.userId} item={item} />
      ))}
    </div>
  );
}

function PhotoModerationCard({ item }: { item: PhotoModerationItemDto }) {
  const [comment, setComment] = useState('');
  const decide = useDecidePhotoModeration();

  const onApprove = () => {
    decide.mutate(
      { userId: item.userId, body: { decision: 'APPROVE' } },
      { onSuccess: () => toast.success(`Фото ${item.displayName} одобрено`) },
    );
  };

  const onHide = () => {
    decide.mutate(
      {
        userId: item.userId,
        body: { decision: 'HIDE', comment: comment.trim() || null },
      },
      { onSuccess: () => toast.success(`Фото ${item.displayName} скрыто`) },
    );
  };

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border bg-card">
      <div className="aspect-square w-full bg-muted">
        {item.photoUrl ? (
          <img
            src={item.photoUrl}
            alt={item.displayName}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">нет фото</div>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 p-3">
        <div>
          <div className="font-medium">
            {item.displayName}
            {item.age != null && <span className="text-muted-foreground"> · {item.age}</span>}
          </div>
          {item.username && <div className="text-xs text-muted-foreground">@{item.username}</div>}
          <div className="text-xs text-muted-foreground tabular-nums">
            {formatEnergyFull(item.energyAvailable)} ⚡
          </div>
        </div>

        {item.bio && <p className="line-clamp-3 text-sm text-muted-foreground">{item.bio}</p>}

        {item.interests.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {item.interests.map((slug) => (
              <Badge key={slug} variant="secondary" className="text-xs">
                {interestLabel(slug)}
              </Badge>
            ))}
          </div>
        )}

        <Input
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Причина скрытия (необязательно)"
          maxLength={500}
          className="mt-auto"
        />

        <div className="flex gap-2">
          <Button type="button" size="sm" className="flex-1" disabled={decide.isPending} onClick={onApprove}>
            Одобрить
          </Button>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            className="flex-1"
            disabled={decide.isPending}
            onClick={onHide}
          >
            Скрыть
          </Button>
        </div>
      </div>
    </div>
  );
}
